import { useEffect, useState } from 'react'
import type { LoginResponse } from '../api/auth'
import { supplyPointTypesApi } from '../api/customers'
import type { SupplyPointFieldDef, SupplyPointType } from '../api/customers'
import {
  Page,
  PageHeader,
  Button,
  IconButton,
  Callout,
  LoadingState,
  Card,
  DataTable,
  StatusPill,
  Dialog,
  TextField,
  Select,
  Checkbox,
  FormSection,
} from '../components/ui'

/**
 * F-111 (TASK-0365, REQ-006-009, REQ-001-003): tipi di punto di fornitura e campi di settore decisi dall'Admin.
 * Un settore nuovo (es. "acqua") si crea qui, scegliendo quali campi porta (potenza, consumo, contatore, linea,
 * migrazione...), senza rilasciare codice. Lettura per tutti, scrittura solo Admin (il server risponde 403 agli
 * altri). Senza nessun tipo configurato valgono i quattro storici (POD, PDR, Telefonia, Fibra): per cambiarne uno
 * si salva una riga con lo stesso codice. Un tipo disattivato non si offre piu' per i punti nuovi; quelli che lo
 * hanno gia' restano modificabili.
 */
interface FormState {
  id?: number | null
  code: string
  label: string
  group: string
  fields: string[]
  sortOrder: number
  isActive: boolean
  fromFallback: boolean
}

const EMPTY_FORM: FormState = {
  code: '',
  label: '',
  group: 'altro',
  fields: [],
  sortOrder: 100,
  isActive: true,
  fromFallback: false,
}

const GROUP_LABELS: Record<string, string> = { energia: 'Energia', telefonia: 'Telefonia', altro: 'Altro' }

function isAdminRole(auth: LoginResponse): boolean {
  return auth.role === 'Administrator' || auth.role === 'EqualAdmin'
}

export function SupplyPointTypesPage({ auth }: { auth: LoginResponse }) {
  const canWrite = isAdminRole(auth)
  const [items, setItems] = useState<SupplyPointType[]>([])
  const [catalog, setCatalog] = useState<SupplyPointFieldDef[]>([])
  const [groups, setGroups] = useState<string[]>(['energia', 'telefonia', 'altro'])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const [list, fields] = await Promise.all([supplyPointTypesApi.list(canWrite), supplyPointTypesApi.fieldCatalog()])
      setItems(list.items)
      setCatalog(fields.items)
      setGroups(fields.groups)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function openCreate() {
    setForm(EMPTY_FORM)
    setModalError(null)
    setShowModal(true)
  }

  function openEdit(t: SupplyPointType) {
    setForm({
      id: t.id,
      code: t.code,
      label: t.label,
      group: t.group,
      fields: [...t.fieldKeys],
      sortOrder: t.sortOrder,
      isActive: t.isActive,
      fromFallback: t.isFallback,
    })
    setModalError(null)
    setShowModal(true)
  }

  function closeModal() {
    if (!saving) setShowModal(false)
  }

  function toggleField(key: string) {
    setForm(f => ({
      ...f,
      fields: f.fields.includes(key) ? f.fields.filter(k => k !== key) : [...f.fields, key],
    }))
  }

  async function handleSave() {
    setSaving(true)
    setModalError(null)
    try {
      const body = {
        code: form.code.trim(),
        label: form.label.trim(),
        group: form.group,
        fields: form.fields,
        sortOrder: form.sortOrder,
        isActive: form.isActive,
      }
      if (form.id != null) await supplyPointTypesApi.update(form.id, body)
      else await supplyPointTypesApi.create(body)
      setShowModal(false)
      setNotice('Tipo salvato.')
      await load()
    } catch (e) {
      setModalError(e instanceof Error ? e.message : 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  async function handleToggleActive(t: SupplyPointType) {
    setError(null)
    setNotice(null)
    try {
      if (t.id != null) {
        await supplyPointTypesApi.setActive(t.id, !t.isActive)
      } else {
        // Tipo storico di ripiego: per ritirarlo si salva una riga con lo stesso codice.
        await supplyPointTypesApi.create({
          code: t.code,
          label: t.label,
          group: t.group,
          fields: t.fieldKeys,
          sortOrder: t.sortOrder,
          isActive: !t.isActive,
        })
      }
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : "Errore nell'aggiornamento")
    }
  }

  const labelOf = (key: string) => catalog.find(c => c.key === key)?.label ?? key

  return (
    <Page>
      <PageHeader
        title="Tipi di fornitura"
        icon="electrical_services"
        back={{ to: '/settings', label: 'Impostazioni generali' }}
        actions={
          canWrite ? (
            <Button variant="filled" icon="add" onClick={openCreate}>
              Nuovo tipo
            </Button>
          ) : undefined
        }
      />

      <Callout tone="info">Ogni tipo di punto di fornitura porta i suoi campi di settore. I tipi storici (POD, PDR, Telefonia, Fibra) valgono finché non ne salvi uno con lo stesso codice; un tipo nuovo non li toglie.</Callout>

      {error && <Callout tone="error">{error}</Callout>}
      {notice && <Callout tone="success">{notice}</Callout>}

      <Card variant="outlined" padding="none">
        {loading ? (
          <LoadingState />
        ) : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Codice</th>
                <th>Etichetta</th>
                <th>Gruppo</th>
                <th>Campi</th>
                <th>Ordine</th>
                <th>Stato</th>
                {canWrite && <th />}
              </tr>
            </thead>
            <tbody>
              {items.map(t => (
                <tr key={t.code} className={t.isActive ? '' : 'text-muted'}>
                  <td><code>{t.code}</code></td>
                  <td>
                    {t.label}
                    {t.isFallback && <StatusPill tone="neutral" className="ms-2">storico</StatusPill>}
                  </td>
                  <td>{GROUP_LABELS[t.group] ?? t.group}</td>
                  <td className="md-typescale-body-small">{t.fieldKeys.length === 0 ? '—' : t.fieldKeys.map(labelOf).join(', ')}</td>
                  <td className="md-num">{t.sortOrder}</td>
                  <td>
                    <StatusPill tone={t.isActive ? 'success' : 'neutral'}>
                      {t.isActive ? 'Attivo' : 'Disattivato'}
                    </StatusPill>
                  </td>
                  {canWrite && (
                    <td className="text-end text-nowrap">
                      <IconButton icon="edit" size="sm" aria-label="Modifica" onClick={() => openEdit(t)} />{' '}
                      <IconButton
                        icon={t.isActive ? 'toggle_on' : 'toggle_off'}
                        size="sm"
                        aria-label={t.isActive ? 'Disattiva' : 'Attiva'}
                        onClick={() => void handleToggleActive(t)}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>

      <Dialog as="form"
 onSubmit={(e) => { e.preventDefault(); void handleSave() }}
        open={showModal}
        onClose={closeModal}
        dismissible={!saving}
        title={form.id != null || form.fromFallback ? 'Modifica tipo' : 'Nuovo tipo'}
        actions={
          <>
            <Button variant="text" onClick={closeModal} disabled={saving}>
              Annulla
            </Button>
            <Button
              type="submit"
              variant="text"
              disabled={saving || !form.label.trim() || !form.code.trim()}
            >
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
        <>
          {modalError && <Callout tone="error">{modalError}</Callout>}

          <FormSection title="Tipo">
            <TextField
              className="md-span-full"
              id="spt-code"
              label="Codice *"
              type="text"
              value={form.code}
              maxLength={20}
              supportingText="es. acqua. Minuscolo, con lettere, cifre, trattino e underscore. Non si cambia più dopo la creazione."
              onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
              disabled={saving || form.id != null || form.fromFallback}
            />
            <TextField
              className="md-span-full"
              id="spt-label"
              label="Etichetta *"
              type="text"
              value={form.label}
              maxLength={100}
              onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
              disabled={saving}
            />
            <Select
              id="spt-group"
              label="Gruppo"
              value={form.group}
              onChange={e => setForm(f => ({ ...f, group: e.target.value }))}
              disabled={saving}
            >
              {groups.map(g => (
                <option key={g} value={g}>{GROUP_LABELS[g] ?? g}</option>
              ))}
            </Select>
            <TextField
              id="spt-order"
              label="Ordine"
              type="number"
              value={form.sortOrder}
              onChange={e => setForm(f => ({ ...f, sortOrder: parseInt(e.target.value, 10) || 0 }))}
              disabled={saving}
            />
          </FormSection>

          <FormSection
            title="Campi del tipo"
            description="Un punto di fornitura di questo tipo salva solo i campi scelti; gli altri vengono azzerati dal server."
          >
            {catalog.map(c => (
              <Checkbox
                key={c.key}
                id={`spt-f-${c.key}`}
                label={c.label}
                checked={form.fields.includes(c.key)}
                onChange={() => toggleField(c.key)}
                disabled={saving}
              />
            ))}
          </FormSection>

          <FormSection title="Opzioni" columns={1}>
            <Checkbox
              id="spt-active"
              label="Attivo (selezionabile per i punti nuovi)"
              checked={form.isActive}
              onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
              disabled={saving}
            />
          </FormSection>
        </>
      </Dialog>
    </Page>
  )
}
